"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
  TestimonialItem,
} from "@/components/feature-template/types";
import { MapPin, Sparkles, TrendingUp } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/resources/hiring-trends";
const APP = "/sign-up";

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "in-demand-skills",
    name: "In-Demand Skills",
    title: "Learn the Skills Employers Want Now",
    description:
      "See which skills appear most in postings for your role, which are growing fastest and which are often asked for together.",
    imageSrcUrl: `${IMG}/hero-skills.png`,
    primaryActionText: "Explore Trends",
    primaryActionLink: APP,
    secondaryActionText: "Browse Jobs",
    secondaryActionLink: "/jobs/board",
  },
  {
    id: "role-demand",
    name: "Role Demand",
    title: "Find Where Your Role Is Hiring",
    description:
      "Open roles for your job title by city and industry, with the split between on-site, hybrid and remote work.",
    imageSrcUrl: `${IMG}/hero-role-demand.png`,
    primaryActionText: "Explore Trends",
    primaryActionLink: APP,
    secondaryActionText: "Browse Jobs",
    secondaryActionLink: "/jobs/board",
  },
  {
    id: "emerging-roles",
    name: "Emerging Roles",
    title: "Spot New Roles Before Everyone Else",
    description:
      "New job titles appearing in postings, with the skills they ask for, typical pay and how fast they are growing.",
    imageSrcUrl: `${IMG}/hero-emerging.png`,
    primaryActionText: "Explore Trends",
    primaryActionLink: APP,
    secondaryActionText: "Salary Insights",
    secondaryActionLink: "/resources/salary-insights",
  },
];

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "skill-trends",
    title: "Skill Trends",
    subtitle: "RISING AND COOLING",
    description:
      "Track how often each skill appears in postings month by month. See what is rising, what is cooling, and which missing skill would open up the most roles for you.",
    benefits: [
      "12-month trend lines",
      "Rising and cooling skills",
      "Skills often paired together",
      "Gaps based on your resume",
    ],
    primaryCTA: "See Skill Trends",
    primaryLink: APP,
    secondaryCTA: "Score My Resume",
    secondaryLink: "/features/resume-jobfit-scorer",
    imageSrcUrl: `${IMG}/skill-trends.png`,
    icon: <TrendingUp className="w-4 h-4" />,
    stats: "Based on real postings",
  },
  {
    id: "role-demand-city",
    title: "Role Demand by City",
    subtitle: "WHERE THE OPENINGS ARE",
    description:
      "Open roles for your title by city, industry and company size, with month-on-month change and the on-site, hybrid and remote split for each.",
    benefits: [
      "Open roles by city",
      "Month-on-month change",
      "Industry and company size",
      "Work-type split",
    ],
    primaryCTA: "View Role Demand",
    primaryLink: APP,
    secondaryCTA: "Research Companies",
    secondaryLink: "/features/jobs-deep-research",
    imageSrcUrl: `${IMG}/role-demand-city.png`,
    icon: <MapPin className="w-4 h-4" />,
    stats: "City, industry, work type",
    orientation: "reversed",
  },
];

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "emerging-roles-watch",
    title: "Emerging Roles Watch",
    subtitle: "THE NEXT WAVE",
    description:
      "New titles such as AI evaluation engineer and design engineer, with what the job involves, the skills it asks for, typical pay and recent postings. See how many of those skills you already have.",
    benefits: [
      "What the role involves",
      "Skills it asks for",
      "Typical pay range",
      "Your skill match",
    ],
    primaryCTA: "See Emerging Roles",
    primaryLink: APP,
    secondaryCTA: "Salary Insights",
    secondaryLink: "/resources/salary-insights",
    imageSrcUrl: `${IMG}/emerging-roles.png`,
    icon: <Sparkles className="w-4 h-4" />,
    stats: "New titles each quarter",
  },
];

// Replace with real user quotes before publishing.
const testimonials: TestimonialItem[] = [
  {
    id: 1,
    text: "Skill trends showed Kubernetes in a third of the postings I wanted. I spent a month on it, added it to my resume and my match scores jumped.",
    name: "Rahul V.",
    role: "Backend Engineer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 2,
    text: "I didn't know Hyderabad had so many hybrid design roles. I widened my search and had two interviews within a fortnight.",
    name: "Sneha P.",
    role: "UX Researcher",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 3,
    text: "The emerging roles list is how I found analytics engineering. It fit my SQL background better than anything I was applying to.",
    name: "Jalen Kyle",
    role: "Project Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&h=800&fit=crop&q=80",
  },
];

export default function HiringTrendsPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "See Where the Market",
            titleHighlight: "Is Heading.",
            subtitle:
              "The skills employers ask for most, where your role is hiring and the new job titles appearing in postings, so you prepare for where demand is going.",
            ctaText: "Explore Trends",
            ctaLink: APP,
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Hiring Data You Can Act On.",
            sectionSubtitle:
              "Decide what to learn and where to apply from what employers are posting.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "testimonials",
          data: {
            title: "Searching With the Market",
            subtitle: "From job seekers who followed the demand",
            testimonials,
          },
        },
        {
          type: "faq",
          data: {
            title: "Questions About Hiring Trends",
            subtitle: "Where the data comes from and how to use it.",
            faqs,
            ctaTitle: "Find out what employers want this month.",
            ctaText: "Explore Trends",
            ctaLink: APP,
          },
        },
      ]}
    />
  );
}
